import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useBrand } from '../contexts/BrandContext';
import { SEO, BreadcrumbSchema } from '../components/SEO';
import { ArrowLeft, Clock, Eye, Calendar, User, Tag, BookOpen, Headphones, ArrowRight } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function BlogPostPage() {
    const { slug } = useParams();
    const brand = useBrand();
    const [post, setPost] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        (async () => {
            try {
                const res = await fetch(`${API}/api/blog/posts/${slug}`);
                if (res.ok) setPost(await res.json());
            } catch (e) { console.error(e); }
            finally { setLoading(false); }
        })();
    }, [slug]);

    if (loading) return <div className="min-h-screen bg-[#0A0D14] flex items-center justify-center text-white/20">Loading...</div>;
    if (!post) return <div className="min-h-screen bg-[#0A0D14] flex items-center justify-center text-white/30">Post not found</div>;

    const publishDate = post.published_at ? new Date(post.published_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }) : '';
    const pageUrl = typeof window !== 'undefined' ? window.location.href : '';

    const articleSchema = {
        "@context": "https://schema.org",
        "@type": "Article",
        "headline": post.meta_title || post.title,
        "description": post.meta_description || post.excerpt,
        "image": post.cover_image,
        "author": { "@type": "Person", "name": post.author_name },
        "publisher": { "@type": "Organization", "name": "Voxoora" },
        "datePublished": post.published_at,
        "dateModified": post.updated_at,
        "url": pageUrl,
        "mainEntityOfPage": pageUrl
    };

    return (
        <div className="min-h-screen bg-[#0A0D14]">
            <SEO
                title={post.meta_title || post.title}
                description={post.meta_description || post.excerpt}
                image={post.cover_image}
                url={pageUrl}
                type="article"
            />
            <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
            <BreadcrumbSchema items={[
                { name: 'Home', url: `${typeof window !== 'undefined' ? window.location.origin : ''}/` },
                { name: 'Blog', url: `${typeof window !== 'undefined' ? window.location.origin : ''}/blog` },
                { name: post.title }
            ]} />

            <header className="fixed top-0 left-0 right-0 z-50 glass">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <Link to="/blog" className="text-white/40 hover:text-white"><ArrowLeft className="w-5 h-5" /></Link>
                        <Link to="/" className="flex items-center gap-2">
                            {brand.logo_url ? (
                                <img src={brand.logo_url} alt={brand.platform_name} className="h-7 max-w-[120px] object-contain" />
                            ) : (
                                <span className="font-outfit font-semibold text-white">{brand.platform_name}</span>
                            )}
                        </Link>
                    </div>
                    <Link to="/blog" className="text-gold text-xs hover:text-gold/80 flex items-center gap-1"><BookOpen className="w-3.5 h-3.5" /> All Articles</Link>
                </div>
            </header>

            <article className="pt-20 pb-16">
                {/* Cover Image */}
                {post.cover_image && (
                    <div className="relative h-[280px] sm:h-[400px] overflow-hidden">
                        <img src={post.cover_image} alt={post.title} className="w-full h-full object-cover" />
                        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0D14] via-[#0A0D14]/40 to-transparent" />
                    </div>
                )}

                <div className="max-w-3xl mx-auto px-4 sm:px-6 -mt-24 relative z-10">
                    {/* Meta */}
                    <div className="flex items-center gap-3 mb-4 flex-wrap">
                        {post.category && <span className="text-[10px] text-gold bg-gold/10 px-2.5 py-1 rounded-full font-medium">{post.category}</span>}
                        {publishDate && <span className="text-[10px] text-white/30 flex items-center gap-1"><Calendar className="w-2.5 h-2.5" />{publishDate}</span>}
                        <span className="text-[10px] text-white/30 flex items-center gap-1"><Clock className="w-2.5 h-2.5" />{post.reading_time || 1} min read</span>
                        <span className="text-[10px] text-white/30 flex items-center gap-1"><Eye className="w-2.5 h-2.5" />{post.views || 0} views</span>
                    </div>

                    {/* Title */}
                    <h1 className="font-outfit text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-4 leading-tight">{post.title}</h1>

                    {/* Author */}
                    <div className="flex items-center gap-3 mb-8 pb-8 border-b border-white/10">
                        <div className="w-10 h-10 rounded-full bg-gold/15 flex items-center justify-center text-gold font-semibold text-sm">
                            {post.author_name?.charAt(0) || 'V'}
                        </div>
                        <div>
                            <p className="text-sm text-white font-medium">{post.author_name}</p>
                            <p className="text-xs text-white/30">Voxoora Travel Insider</p>
                        </div>
                    </div>

                    {/* Content */}
                    <div className="blog-content prose prose-invert prose-lg max-w-none
                        prose-headings:font-outfit prose-headings:text-white prose-headings:font-semibold
                        prose-h2:text-xl prose-h2:sm:text-2xl prose-h2:mt-10 prose-h2:mb-4
                        prose-h3:text-lg prose-h3:mt-8 prose-h3:mb-3
                        prose-p:text-white/70 prose-p:leading-relaxed prose-p:mb-4
                        prose-li:text-white/60 prose-li:marker:text-gold
                        prose-strong:text-white prose-em:text-gold/80
                        prose-a:text-gold prose-a:no-underline prose-a:hover:underline"
                        dangerouslySetInnerHTML={{ __html: post.content }}
                        data-testid="blog-content"
                    />

                    {/* Tags */}
                    {post.tags?.length > 0 && (
                        <div className="mt-10 pt-6 border-t border-white/10">
                            <div className="flex items-center gap-2 flex-wrap">
                                <Tag className="w-3.5 h-3.5 text-white/20" />
                                {post.tags.map(tag => (
                                    <Link key={tag} to={`/blog?tag=${encodeURIComponent(tag)}`}
                                        className="px-2.5 py-1 rounded-full text-[10px] bg-white/5 text-white/40 border border-white/10 hover:border-gold/20 hover:text-gold transition-colors">
                                        {tag}
                                    </Link>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Related Posts */}
                    {post.related_posts?.length > 0 && (
                        <div className="mt-12 pt-8 border-t border-white/10">
                            <h3 className="font-outfit font-semibold text-white text-lg mb-6">Related Articles</h3>
                            <div className="grid sm:grid-cols-3 gap-4">
                                {post.related_posts.map(rp => (
                                    <Link key={rp.slug} to={`/blog/${rp.slug}`}
                                        className="group bg-white/[0.03] border border-white/[0.06] rounded-xl overflow-hidden hover:border-gold/20 transition-all">
                                        {rp.cover_image && <img src={rp.cover_image} alt={rp.title} className="w-full h-28 object-cover group-hover:scale-105 transition-transform duration-500" />}
                                        <div className="p-3">
                                            <h4 className="text-xs font-medium text-white group-hover:text-gold transition-colors line-clamp-2">{rp.title}</h4>
                                            <span className="text-[10px] text-white/30 mt-1 block">{rp.reading_time || 1} min read</span>
                                        </div>
                                    </Link>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* CTA */}
                    <div className="mt-12 p-6 rounded-2xl bg-gradient-to-r from-gold/[0.08] to-gold/[0.03] border border-gold/15 text-center">
                        <Headphones className="w-8 h-8 text-gold mx-auto mb-3" />
                        <h3 className="font-outfit font-semibold text-white text-lg mb-2">Ready to Explore?</h3>
                        <p className="text-sm text-white/50 mb-4">Book your audio guide and skip-the-line tickets today.</p>
                        <Link to="/">
                            <button className="bg-gold text-[#0A0D14] hover:bg-gold/90 px-6 py-2.5 rounded-lg text-sm font-semibold inline-flex items-center gap-2">
                                Browse Experiences <ArrowRight className="w-4 h-4" />
                            </button>
                        </Link>
                    </div>
                </div>
            </article>
        </div>
    );
}
